import {
  Component,
  EventEmitter,
  Input,
  Output,
  forwardRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { IconProp } from '@fortawesome/fontawesome-svg-core';

export interface ToggleButton {
  label: string;
  icon?: IconProp;
  background?: string;
  value: any;
  isActive: boolean;
  visible?: boolean;
}

@Component({
  selector: 'app-form-toggle-buttons',
  templateUrl: './form-toggle-buttons.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormToggleButtonsComponent),
      multi: true,
    },
  ],
  styleUrls: ['./form-toggle-buttons.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FormToggleButtonsComponent implements ControlValueAccessor {
  @Input() buttons: ToggleButton[] = [];

  private onChange: (value: any) => void;
  private onTouched: () => void;
  @Input() isCheckbox: boolean = false; // Determines if the toggle buttons should behave like a checkbox

  @Output() onValueChanged = new EventEmitter<any>();

  private value: any;
  disabled: boolean;

  writeValue(value: any): void {
    this.value = value;
    this.updateButtonStates();
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onButtonClick(button: ToggleButton): void {
    if (this.isCheckbox) {
      const currentValue = Array.isArray(this.value) ? [...this.value] : [];
      const index = currentValue.indexOf(button.value);
      if (index > -1) {
        currentValue.splice(index, 1); // Remove the value
      } else {
        currentValue.push(button.value); // Add the value
      }
      this.value = currentValue;
    } else {
      this.value = button.value; // Radio behavior
    }
    this.onValueChanged.emit(this.value);
    this.updateButtonStates();
    this.onChange(this.value);
    this.onTouched();
  }

  private updateButtonStates(): void {
    this.buttons.forEach((button) => {
      if (this.isCheckbox) {
        button.isActive =
          Array.isArray(this.value) && this.value.includes(button.value);
      } else {
        button.isActive = button.value === this.value;
      }
    });
  }
}
